import BuildIcon from '@mui/icons-material/Build';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

export interface RepairJobActionProps {
  /** Serial of the part the repair job would start from. Absent for a row
   *  whose serial column is hidden — the link still works, unprefilled. */
  serial1?: string;
  /** Used only to describe the control to screen readers. */
  label: string;
  /** Consumable rows keep the control but cannot act on it. */
  disabled: boolean;
  onClick: (e: React.MouseEvent) => void;
}

// The accent colour has to yield to the theme's disabled colour, otherwise the
// sx value wins over MUI's own .Mui-disabled rule and a disabled wrench still
// reads as actionable.
const ICON_SX = {
  color: 'v4.accent',
  '&.Mui-disabled': { color: 'action.disabled' },
} as const;

const UNAVAILABLE_REASON = 'Repair jobs are only available for serialised parts';

/**
 * The Create Repair Job wrench on a parts-list row.
 *
 * A consumable part can't have a repair job, but the control is disabled rather
 * than removed: the action column stays aligned down the table, and hovering
 * says why it's unavailable instead of the button silently not being there.
 */
export const RepairJobAction: React.FC<RepairJobActionProps> = ({
  serial1,
  label,
  disabled,
  onClick,
}) => (
  <Tooltip title={disabled ? UNAVAILABLE_REASON : 'Create Repair Job'} placement="left">
    {/* A disabled MUI button fires no events, so the tooltip needs a wrapper
        element to hang its listeners on. */}
    <span>
      {disabled ? (
        // A disabled button can't take focus, so the tooltip is unreachable by
        // keyboard — the reason is carried in the label instead, where a screen
        // reader browsing the row will still find it.
        <IconButton
          size="small"
          disabled
          aria-label={`${label} — unavailable. ${UNAVAILABLE_REASON}`}
          sx={ICON_SX}
        >
          <BuildIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ) : (
        <IconButton
          size="small"
          component={RouterLink}
          to={`/parts/createRepair${serial1 ? `?serial=${encodeURIComponent(serial1)}` : ''}`}
          onClick={onClick}
          aria-label={label}
          sx={ICON_SX}
        >
          <BuildIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </span>
  </Tooltip>
);
